import React, { useState, useEffect } from 'react';
import SearchForm from './Search';
import Results from './Results';
import './App.css';

// 根据环境选择 API URL
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://market-grabber-827891184210.europe-west2.run.app'  // Cloud Run 服务 URL
  : 'http://localhost:8080';  // 修改为正确的端口

// 添加调试日志
console.log('Current environment:', process.env.NODE_ENV);
console.log('API Base URL:', API_BASE_URL);

function App() {
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [apiStatus, setApiStatus] = useState('checking');
  const [progress, setProgress] = useState(null);
  const [searchId, setSearchId] = useState(null);
  const [pollInterval, setPollInterval] = useState(10000); // 初始轮询间隔为15秒

  useEffect(() => {
    // Check API availability on component mount
    const checkApiStatus = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/health`);
        if (response.ok) {
          setApiStatus('available');
        } else {
          setApiStatus('error');
          setError('API is not responding correctly');
        }
      } catch (err) {
        setApiStatus('error');
        setError('Cannot connect to API server');
      }
    };
    checkApiStatus();
  }, []);

  // 进度检查的 useEffect
  useEffect(() => {
    let progressInterval;
    let retryCount = 0;
    const MAX_RETRY_COUNT = 3;
    const MAX_INTERVAL = 10000; // 最大轮询间隔10秒
    
    if (searchId) {
      console.log('Starting progress tracking for searchId:', searchId);
      
      // 设置初始进度状态
      setProgress({
        current_step: 0,
        current_step_name: 'Initializing search',
        percentage: 0,
        total_steps: 5
      });
      
      const checkProgress = async () => {
        try {
          console.log('Checking progress for searchId:', searchId, 'Current interval:', pollInterval);
          const response = await fetch(`${API_BASE_URL}/api/progress/${searchId}`);
          console.log('Progress response status:', response.status);
          
          if (response.status === 429) {
            // 如果遇到限流，增加轮询间隔
            const newInterval = Math.min(pollInterval * 2, MAX_INTERVAL);
            console.log(`Rate limited, increasing poll interval to ${newInterval}ms`);
            setPollInterval(newInterval);
            return;
          }
          
          if (!response.ok) {
            throw new Error('Failed to fetch progress');
          }
          
          // 成功获取数据后，如果之前增加过间隔，可以适当减少
          if (pollInterval > 2000) {
            setPollInterval(Math.max(2000, pollInterval / 2));
          }
          
          const data = await response.json();
          console.log('Progress data:', data);
          
          if (!data) {
            console.error('No progress data received');
            return;
          }
          
          // 重置重试计数
          retryCount = 0;
          
          // 更新进度状态
          if (data.status === 'in_progress') {
            setProgress({
              current_step: data.current_step,
              current_step_name: data.current_step_name,
              percentage: data.percentage,
              total_steps: data.total_steps
            });
          } else if (data.status === 'error') {
            console.error('Progress error:', data.error);
            clearInterval(progressInterval);
            setError(data.error || 'An error occurred during search');
            setIsLoading(false);
          } else if (data.status === 'completed') {
            console.log('Search completed');
            
            // 设置最终进度状态
            setProgress({
              current_step: data.total_steps,
              current_step_name: 'Search completed',
              percentage: 100,
              total_steps: data.total_steps
            });
            
            if (data.results) {
              setResults({
                data: data.results,
                search_id: searchId,
                status: 'success'
              });
            }
            
            clearInterval(progressInterval);
            setIsLoading(false);
          }
        } catch (error) {
          console.error('Error checking progress:', error);
          retryCount++;
          
          if (retryCount >= MAX_RETRY_COUNT) {
            console.error(`Failed after ${MAX_RETRY_COUNT} retries, increasing polling interval`);
            const newInterval = Math.min(pollInterval * 2, MAX_INTERVAL);
            setPollInterval(newInterval);
            retryCount = 0;
          }
          
          console.log(`Will retry progress check in ${pollInterval}ms (attempt ${retryCount}/${MAX_RETRY_COUNT})`);
        }
      };
      
      // 立即执行一次
      checkProgress();
      
      // 设置定时器
      progressInterval = setInterval(checkProgress, pollInterval);
      console.log('Progress interval set up with interval:', pollInterval);
      
      return () => {
        if (progressInterval) {
          console.log('Cleaning up progress interval');
          clearInterval(progressInterval);
        }
      };
    }
  }, [searchId, pollInterval]); // 添加 pollInterval 作为依赖

  const handleSearch = async (searchParams) => {
    setIsLoading(true);
    setError(null);
    setResults(null);
    setProgress(null);
    setSearchId(null);
    setPollInterval(10000);
    
    try {
      console.log('Sending search request to:', `${API_BASE_URL}/api/search`);
      console.log('Search parameters:', searchParams);
      
      const response = await fetch(`${API_BASE_URL}/api/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          keywords: searchParams.keywords,
          region: searchParams.region
        }),
      });
      
      console.log('Search response status:', response.status);
      
      if (!response.ok) {
        throw new Error('Search request failed');
      }
      
      const data = await response.json();
      console.log('Search response data:', data);
      
      if (data.search_id) {
        console.log('Setting search ID:', data.search_id);
        setSearchId(data.search_id);
      } else {
        throw new Error('No search ID provided in response');
      }
    } catch (error) {
      console.error('Search error:', error);
      setError(error.message || 'An error occurred during search');
      setIsLoading(false);
    }
  };

  if (apiStatus === 'checking') {
    return (
      <div className="container text-center my-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Checking API connection...</p>
      </div>
    );
  }

  if (apiStatus === 'error') {
    return (
      <div className="container">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Connection Error</h4>
          <p>Unable to connect to the API server. Please try again later.</p>
          <hr />
          <p className="mb-0">If this problem persists, please contact support.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <div className="container">
        <SearchForm 
          onSearch={handleSearch} 
          isLoading={isLoading} 
          progress={progress}
        />
        
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
        
        {results && <Results data={results} />}
      </div>
    </div>
  );
}

export default App;
